import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, Plus, MoreHorizontal, Pencil, Trash2, KeyRound,
  ArrowLeft, Users, Search, Copy, BarChart3, MessageCircle,
  TrendingUp, ThumbsUp, ThumbsDown, Activity, BookOpen, Upload,
  FileText, Globe, Target, DollarSign
} from "lucide-react";
import { toast } from "sonner";
import { DeepUsageCard } from "@/components/DeepUsageCard";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area
} from "recharts";

const PROGRAM_OPTIONS = [
  { value: "odyss_start", label: "Odyss'Start" },
  { value: "odyss_mentoring", label: "Odyss'Mentoring" },
  { value: "major", label: "Major" },
];

const AGENT_OPTIONS = [
  { value: "global", label: "Tous les agents (Global)" },
  { value: "routeur", label: "ODYS'ROUTEUR" },
  { value: "analyse", label: "ODYS'ANALYSE" },
  { value: "ia", label: "ODYS'ASSISTANT" },
  { value: "strategie", label: "ODYS'STRATEGIE" },
  { value: "nego", label: "ODYS'NEGO" },
  { value: "banque", label: "ODYS'BANQUE" },
  { value: "travaux", label: "ODYS'TRAVAUX" },
  { value: "fiscalite", label: "ODYS'FISCALITE" },
  { value: "locataire", label: "ODYS'LOCATAIRE" },
];

const CATEGORY_OPTIONS = [
  { value: "methodologie", label: "Methodologie" },
  { value: "ressources", label: "Ressources" },
  { value: "exemples", label: "Exemples" },
  { value: "document", label: "Document" },
  { value: "general", label: "General" },
];

export default function AdminPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [tempPw, setTempPw] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Knowledge base state
  const [knowledge, setKnowledge] = useState([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [createKnowledgeDialog, setCreateKnowledgeDialog] = useState(false);
  const [editKnowledgeDialog, setEditKnowledgeDialog] = useState(null);
  const [deleteKnowledgeDialog, setDeleteKnowledgeDialog] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Knowledge form
  const [kTitle, setKTitle] = useState("");
  const [kContent, setKContent] = useState("");
  const [kAgent, setKAgent] = useState("global");
  const [kCategory, setKCategory] = useState("general");

  // Create form
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newProgram, setNewProgram] = useState("odyss_start");

  // Edit form
  const [editName, setEditName] = useState("");
  const [editProgram, setEditProgram] = useState("");

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    loadStudents();
    loadAnalytics();
    loadKnowledge();
  }, []);

  const loadStudents = async () => {
    try {
      const res = await api().get("/admin/students");
      setStudents(res.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api().get("/admin/analytics");
      setAnalytics(res.data);
    } catch {
      // Analytics may fail if no data yet
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadKnowledge = async () => {
    setKnowledgeLoading(true);
    try {
      const res = await api().get("/admin/knowledge");
      setKnowledge(res.data);
    } catch {
      // Knowledge may be empty
    } finally {
      setKnowledgeLoading(false);
    }
  };

  const handleCreateKnowledge = async () => {
    if (!kTitle || !kContent) {
      toast.error("Remplis le titre et le contenu");
      return;
    }
    try {
      await api().post("/admin/knowledge", {
        title: kTitle,
        content: kContent,
        agent_type: kAgent === "global" ? null : kAgent,
        category: kCategory,
      });
      toast.success("Connaissance ajoutee !");
      setCreateKnowledgeDialog(false);
      resetKnowledgeForm();
      loadKnowledge();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleUpdateKnowledge = async () => {
    if (!editKnowledgeDialog) return;
    try {
      await api().put(`/admin/knowledge/${editKnowledgeDialog.knowledge_id}`, {
        title: kTitle || undefined,
        content: kContent || undefined,
        agent_type: kAgent === "global" ? null : kAgent,
        category: kCategory || undefined,
      });
      toast.success("Connaissance mise a jour");
      setEditKnowledgeDialog(null);
      resetKnowledgeForm();
      loadKnowledge();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteKnowledge = async () => {
    if (!deleteKnowledgeDialog) return;
    try {
      await api().delete(`/admin/knowledge/${deleteKnowledgeDialog.knowledge_id}`);
      toast.success("Connaissance supprimee");
      setDeleteKnowledgeDialog(null);
      loadKnowledge();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleUploadKnowledge = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", kTitle || file.name);
      formData.append("agent_type", kAgent);
      formData.append("category", kCategory);

      const res = await api().post("/admin/knowledge/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Document importe ! (${res.data.extracted_chars} caracteres extraits)`);
      setUploadDialog(false);
      resetKnowledgeForm();
      loadKnowledge();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const resetKnowledgeForm = () => {
    setKTitle("");
    setKContent("");
    setKAgent("global");
    setKCategory("general");
  };

  const openEditKnowledge = (item) => {
    setKTitle(item.title);
    setKContent(item.content);
    setKAgent(item.agent_type || "global");
    setKCategory(item.category || "general");
    setEditKnowledgeDialog(item);
  };

  const handleCreate = async () => {
    if (!newName || !newEmail) {
      toast.error("Remplis tous les champs");
      return;
    }
    try {
      const res = await api().post("/admin/students", {
        student_name: newName,
        student_email: newEmail,
        program: newProgram,
      });
      setTempPw(res.data.temp_password);
      toast.success("Utilisateur cree !");
      setCreateDialog(false);
      setNewName(""); setNewEmail(""); setNewProgram("odyss_start");
      loadStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleEdit = async () => {
    if (!editDialog) return;
    try {
      await api().put(`/admin/students/${editDialog.student_id}`, {
        student_name: editName || undefined,
        program: editProgram || undefined,
      });
      toast.success("Utilisateur mis a jour");
      setEditDialog(null);
      loadStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await api().delete(`/admin/students/${deleteDialog.student_id}`);
      toast.success("Utilisateur supprime");
      setDeleteDialog(null);
      loadStudents();
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  const handleResetPw = async (studentId) => {
    try {
      const res = await api().post(`/admin/reset-password/${studentId}`);
      setTempPw(res.data.temp_password);
      toast.success("Mot de passe reinitialise");
    } catch {
      toast.error("Erreur");
    }
  };

  const filtered = students.filter(
    (s) =>
      s.student_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.student_email?.toLowerCase().includes(search.toLowerCase())
  );

  const getProgramBadge = (program) => {
    if (program === "odyss_mentoring") return { label: "Mentoring", className: "bg-emerald-100 text-emerald-700 border-0" };
    if (program === "major") return { label: "Major", className: "bg-[#FF8C42]/15 text-[#FF8C42] border-0" };
    return { label: "Start", className: "bg-[#16233B]/10 text-[#16233B] border-0" };
  };

  const a = analytics || {};

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="admin-back-btn"
              className="text-[#6B7280]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-8 h-8 rounded-lg bg-[#16233B] flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-lg font-bold text-[#16233B] font-heading">Admin Panel</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/dashboard")}
              data-testid="test-agents-btn"
              className="text-[#FF8C42] border-[#FF8C42]/30 hover:bg-[#FF8C42]/5"
            >
              <MessageCircle className="w-4 h-4 mr-1" />
              Tester les agents
            </Button>
            <Button
              onClick={() => setCreateDialog(true)}
              data-testid="create-student-btn"
              className="bg-[#16233B] hover:bg-[#162B47] text-white rounded-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              Creer un utilisateur
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Tabs defaultValue="students" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 h-auto rounded-lg">
            <TabsTrigger
              value="students"
              data-testid="tab-students"
              className="data-[state=active]:bg-[#16233B] data-[state=active]:text-white rounded-md px-4 py-2"
            >
              <Users className="w-4 h-4 mr-2" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              data-testid="tab-analytics"
              className="data-[state=active]:bg-[#16233B] data-[state=active]:text-white rounded-md px-4 py-2"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytiques
            </TabsTrigger>
            <TabsTrigger
              value="knowledge"
              data-testid="tab-knowledge"
              className="data-[state=active]:bg-[#16233B] data-[state=active]:text-white rounded-md px-4 py-2"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Base de connaissances
            </TabsTrigger>
          </TabsList>

          {/* STUDENTS TAB */}
          <TabsContent value="students" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <Card className="border-slate-200 shadow-sm rounded-xl">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#16233B]/5 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#16233B]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1F2937]">{students.length}</p>
                    <p className="text-xs text-[#6B7280]">Total</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-sm rounded-xl">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#16233B]/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#16233B]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1F2937]">
                      {students.filter((s) => s.program === "odyss_start").length}
                    </p>
                    <p className="text-xs text-[#6B7280]">Odyss'Start</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-sm rounded-xl">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <Users className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1F2937]">
                      {students.filter((s) => s.program === "odyss_mentoring").length}
                    </p>
                    <p className="text-xs text-[#6B7280]">Mentoring</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-sm rounded-xl">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#FF8C42]/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#FF8C42]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1F2937]">
                      {students.filter((s) => s.program === "major").length}
                    </p>
                    <p className="text-xs text-[#6B7280]">Majors</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Table */}
            <Card className="border-slate-200 shadow-sm rounded-xl">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-4">
                  <CardTitle className="font-heading text-lg">Liste des utilisateurs</CardTitle>
                  <div className="relative w-64">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                    <Input
                      data-testid="admin-search-input"
                      placeholder="Rechercher..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9 h-9 rounded-lg"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {loading ? (
                  <div className="py-12 flex justify-center">
                    <div className="w-6 h-6 border-2 border-[#16233B]/20 border-t-[#16233B] rounded-full animate-spin" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="py-12 text-center text-[#6B7280] text-sm">Aucun utilisateur trouve</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nom</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Inscription</TableHead>
                        <TableHead>Progression</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((s) => {
                        const badge = getProgramBadge(s.program);
                        return (
                          <TableRow key={s.student_id} data-testid={`student-row-${s.student_id}`}>
                            <TableCell className="font-medium text-[#1F2937]">{s.student_name}</TableCell>
                            <TableCell className="text-[#6B7280] text-sm">{s.student_email}</TableCell>
                            <TableCell>
                              <Badge className={`rounded-full text-xs ${badge.className}`}>
                                {badge.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-[#6B7280] text-sm">
                              {s.enrollment_date ? new Date(s.enrollment_date).toLocaleDateString("fr-FR") : "-"}
                            </TableCell>
                            <TableCell className="text-[#6B7280] text-sm">
                              {s.completed_modules?.length || 0}/17
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    data-testid={`student-actions-${s.student_id}`}
                                    className="h-8 w-8"
                                  >
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setEditDialog(s);
                                      setEditName(s.student_name);
                                      setEditProgram(s.program);
                                    }}
                                  >
                                    <Pencil className="w-4 h-4 mr-2" /> Modifier
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleResetPw(s.student_id)}>
                                    <KeyRound className="w-4 h-4 mr-2" /> Reset mot de passe
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => setDeleteDialog(s)}
                                    className="text-red-500 focus:text-red-500"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ANALYTICS TAB */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Link to advanced analytics */}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                onClick={() => navigate("/admin/usage")}
                data-testid="open-usage-dashboard-btn"
                className="bg-[#B5894A] hover:bg-[#8C6635] text-white"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Usage & couts par eleve
              </Button>
              <Button
                onClick={() => navigate("/admin/analytics")}
                className="bg-[#16233B] hover:bg-[#16233B]/90"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics avancees
              </Button>
            </div>
            <DeepUsageCard />
            {analyticsLoading ? (
              <div className="py-20 flex justify-center">
                <div className="w-8 h-8 border-3 border-[#16233B]/20 border-t-[#16233B] rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="border-slate-200 shadow-sm rounded-xl">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#16233B]/5 flex items-center justify-center">
                          <MessageCircle className="w-5 h-5 text-[#16233B]" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#1F2937]">{a.total_conversations || 0}</p>
                          <p className="text-xs text-[#6B7280]">Conversations</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200 shadow-sm rounded-xl">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#FF8C42]/10 flex items-center justify-center">
                          <Activity className="w-5 h-5 text-[#FF8C42]" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#1F2937]">{a.total_messages || 0}</p>
                          <p className="text-xs text-[#6B7280]">Messages echanges</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200 shadow-sm rounded-xl">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                          <ThumbsUp className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#1F2937]">{a.feedback_positive || 0}</p>
                          <p className="text-xs text-[#6B7280]">Retours positifs</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200 shadow-sm rounded-xl">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                          <ThumbsDown className="w-5 h-5 text-red-400" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#1F2937]">{a.feedback_negative || 0}</p>
                          <p className="text-xs text-[#6B7280]">Retours negatifs</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Agent Usage Chart */}
                  <Card className="border-slate-200 shadow-sm rounded-xl">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-[#16233B]" />
                        IA les plus utilisees
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {a.agent_usage?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={a.agent_usage} barSize={40}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="agent" tick={{ fill: "#6B7280", fontSize: 12 }} />
                            <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                            <Tooltip
                              contentStyle={{
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
                              }}
                            />
                            <Bar dataKey="count" fill="#16233B" radius={[6, 6, 0, 0]} name="Conversations" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[250px] flex items-center justify-center text-[#6B7280] text-sm">
                          Pas encore de donnees. Les statistiques apparaitront apres les premieres conversations.
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Daily Activity Chart */}
                  <Card className="border-slate-200 shadow-sm rounded-xl">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#FF8C42]" />
                        Activite quotidienne (14 jours)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {a.daily_activity?.some((d) => d.messages > 0) ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <AreaChart data={a.daily_activity}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 11 }} />
                            <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                            <Tooltip
                              contentStyle={{
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
                              }}
                            />
                            <Area
                              type="monotone"
                              dataKey="messages"
                              stroke="#FF8C42"
                              fill="#FF8C42"
                              fillOpacity={0.1}
                              strokeWidth={2}
                              name="Messages"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[250px] flex items-center justify-center text-[#6B7280] text-sm">
                          Pas encore d'activite. Les donnees apparaitront des les premieres interactions.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Top Students */}
                {a.top_students?.length > 0 && (
                  <Card className="border-slate-200 shadow-sm rounded-xl">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Eleves les plus actifs</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>Eleve</TableHead>
                            <TableHead>Conversations</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {a.top_students.map((s, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-bold text-[#16233B]">{i + 1}</TableCell>
                              <TableCell className="font-medium">{s.name}</TableCell>
                              <TableCell>{s.conversations}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* KNOWLEDGE TAB */}
          <TabsContent value="knowledge" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="border-slate-200 shadow-sm rounded-xl">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#16233B]/5 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-[#16233B]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1F2937]">{knowledge.length}</p>
                    <p className="text-xs text-[#6B7280]">Connaissances</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-sm rounded-xl">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1F2937]">
                      {knowledge.filter(k => !k.agent_type).length}
                    </p>
                    <p className="text-xs text-[#6B7280]">Globales</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-sm rounded-xl">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#FF8C42]/10 flex items-center justify-center">
                    <Target className="w-5 h-5 text-[#FF8C42]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1F2937]">
                      {knowledge.filter(k => k.agent_type).length}
                    </p>
                    <p className="text-xs text-[#6B7280]">Specifiques</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                onClick={() => setCreateKnowledgeDialog(true)}
                data-testid="create-knowledge-btn"
                className="bg-[#16233B] hover:bg-[#162B47] text-white rounded-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une connaissance
              </Button>
              <Button
                variant="outline"
                onClick={() => setUploadDialog(true)}
                data-testid="upload-knowledge-btn"
                className="text-[#FF8C42] border-[#FF8C42]/30 hover:bg-[#FF8C42]/5"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importer un document
              </Button>
            </div>

            {/* Knowledge Table */}
            <Card className="border-slate-200 shadow-sm rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Titre</TableHead>
                    <TableHead className="font-semibold">Agent</TableHead>
                    <TableHead className="font-semibold">Categorie</TableHead>
                    <TableHead className="font-semibold">Statut</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {knowledge.map((item) => (
                    <TableRow key={item.knowledge_id} data-testid={`knowledge-row-${item.knowledge_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#6B7280]" />
                          <span className="font-medium">{item.title}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.agent_type ? "secondary" : "default"} className={
                          item.agent_type 
                            ? "bg-[#FF8C42]/10 text-[#FF8C42] border-0" 
                            : "bg-emerald-100 text-emerald-700 border-0"
                        }>
                          {item.agent_type 
                            ? AGENT_OPTIONS.find(a => a.value === item.agent_type)?.label || item.agent_type
                            : "Global"
                          }
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-[#6B7280] capitalize">{item.category}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.is_active ? "default" : "secondary"} className={
                          item.is_active 
                            ? "bg-emerald-100 text-emerald-700 border-0" 
                            : "bg-slate-100 text-slate-500 border-0"
                        }>
                          {item.is_active ? "Actif" : "Inactif"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditKnowledge(item)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setDeleteKnowledgeDialog(item)}
                              className="text-red-500"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  {knowledge.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-[#6B7280]">
                        Aucune connaissance. Ajoute des informations pour enrichir tes agents IA.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Creer un utilisateur</DialogTitle>
            <DialogDescription>
              Un mot de passe temporaire sera genere automatiquement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nom complet</label>
              <Input
                data-testid="create-student-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Jean Dupont"
                className="h-10 rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <Input
                data-testid="create-student-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="jean@example.com"
                className="h-10 rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Role / Programme</label>
              <div className="flex flex-wrap gap-2">
                {PROGRAM_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    variant={newProgram === opt.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setNewProgram(opt.value)}
                    data-testid={`create-program-${opt.value}`}
                    className={newProgram === opt.value ? "bg-[#16233B]" : ""}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Annuler</Button>
            <Button
              onClick={handleCreate}
              data-testid="create-student-submit"
              className="bg-[#16233B] hover:bg-[#162B47] text-white"
            >
              Creer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier l'utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nom</label>
              <Input
                data-testid="edit-student-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-10 rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Role / Programme</label>
              <div className="flex flex-wrap gap-2">
                {PROGRAM_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    variant={editProgram === opt.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setEditProgram(opt.value)}
                    className={editProgram === opt.value ? "bg-[#16233B]" : ""}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>Annuler</Button>
            <Button
              onClick={handleEdit}
              data-testid="edit-student-submit"
              className="bg-[#16233B] hover:bg-[#162B47] text-white"
            >
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Supprimer l'utilisateur</DialogTitle>
            <DialogDescription>
              Es-tu sur de vouloir supprimer {deleteDialog?.student_name} ? Cette action est irreversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>Annuler</Button>
            <Button
              onClick={handleDelete}
              data-testid="delete-student-confirm"
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp Password Dialog */}
      <Dialog open={!!tempPw} onOpenChange={() => setTempPw(null)}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Mot de passe temporaire</DialogTitle>
            <DialogDescription>
              Communique ce mot de passe a l'utilisateur. Il devra le changer a sa premiere connexion.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 p-3 bg-[#F5F5F5] rounded-lg">
            <code className="flex-1 text-lg font-mono font-bold text-[#16233B]" data-testid="temp-password-display">
              {tempPw}
            </code>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                try {
                  const textArea = document.createElement("textarea");
                  textArea.value = tempPw;
                  textArea.style.position = "fixed";
                  textArea.style.left = "-999999px";
                  document.body.appendChild(textArea);
                  textArea.select();
                  document.execCommand("copy");
                  document.body.removeChild(textArea);
                  toast.success("Copie !");
                } catch {
                  toast.info(`Mot de passe : ${tempPw}`);
                }
              }}
              data-testid="copy-temp-password-btn"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPw(null)} className="bg-[#16233B] text-white">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Knowledge Dialog */}
      <Dialog open={createKnowledgeDialog} onOpenChange={setCreateKnowledgeDialog}>
        <DialogContent className="rounded-xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Ajouter une connaissance</DialogTitle>
            <DialogDescription>
              Ajoute des informations que tes agents IA pourront utiliser.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Titre</label>
              <Input
                data-testid="knowledge-title-input"
                value={kTitle}
                onChange={(e) => setKTitle(e.target.value)}
                placeholder="Ex: Methodologie de negociation Odys'Invest"
                className="h-10 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Agent cible</label>
                <Select value={kAgent} onValueChange={setKAgent}>
                  <SelectTrigger className="h-10 rounded-lg">
                    <SelectValue placeholder="Choisir un agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {AGENT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Categorie</label>
                <Select value={kCategory} onValueChange={setKCategory}>
                  <SelectTrigger className="h-10 rounded-lg">
                    <SelectValue placeholder="Choisir une categorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Contenu</label>
              <Textarea
                data-testid="knowledge-content-input"
                value={kContent}
                onChange={(e) => setKContent(e.target.value)}
                placeholder="Ecris le contenu que l'agent doit connaitre..."
                className="min-h-[200px] rounded-lg"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateKnowledgeDialog(false); resetKnowledgeForm(); }}>
              Annuler
            </Button>
            <Button
              onClick={handleCreateKnowledge}
              data-testid="create-knowledge-submit"
              className="bg-[#16233B] hover:bg-[#162B47] text-white"
            >
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Knowledge Dialog */}
      <Dialog open={!!editKnowledgeDialog} onOpenChange={() => { setEditKnowledgeDialog(null); resetKnowledgeForm(); }}>
        <DialogContent className="rounded-xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier la connaissance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Titre</label>
              <Input
                value={kTitle}
                onChange={(e) => setKTitle(e.target.value)}
                className="h-10 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Agent cible</label>
                <Select value={kAgent} onValueChange={setKAgent}>
                  <SelectTrigger className="h-10 rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGENT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Categorie</label>
                <Select value={kCategory} onValueChange={setKCategory}>
                  <SelectTrigger className="h-10 rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Contenu</label>
              <Textarea
                value={kContent}
                onChange={(e) => setKContent(e.target.value)}
                className="min-h-[200px] rounded-lg"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditKnowledgeDialog(null); resetKnowledgeForm(); }}>
              Annuler
            </Button>
            <Button onClick={handleUpdateKnowledge} className="bg-[#16233B] hover:bg-[#162B47] text-white">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Knowledge Dialog */}
      <Dialog open={!!deleteKnowledgeDialog} onOpenChange={() => setDeleteKnowledgeDialog(null)}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Supprimer la connaissance</DialogTitle>
            <DialogDescription>
              Es-tu sur de vouloir supprimer "{deleteKnowledgeDialog?.title}" ? Cette action est irreversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteKnowledgeDialog(null)}>Annuler</Button>
            <Button
              onClick={handleDeleteKnowledge}
              variant="destructive"
              data-testid="confirm-delete-knowledge"
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Knowledge Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Importer un document</DialogTitle>
            <DialogDescription>
              Le contenu du document sera extrait et ajoute a la base de connaissances.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Titre (optionnel)</label>
              <Input
                value={kTitle}
                onChange={(e) => setKTitle(e.target.value)}
                placeholder="Sera le nom du fichier si non renseigne"
                className="h-10 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Agent cible</label>
                <Select value={kAgent} onValueChange={setKAgent}>
                  <SelectTrigger className="h-10 rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGENT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Categorie</label>
                <Select value={kCategory} onValueChange={setKCategory}>
                  <SelectTrigger className="h-10 rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Fichier (PDF, Word, Excel, TXT)</label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleUploadKnowledge}
                accept=".pdf,.doc,.docx,.txt,.csv,.xlsx"
                className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#16233B] file:text-white file:cursor-pointer"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadDialog(false); resetKnowledgeForm(); }}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
